import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Box, Collapse } from "@mui/material";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import {
  PrecisionManufacturingOutlined,
  LocationOnOutlined,
  GroupsOutlined,
  SearchOutlined,
  PlaceOutlined,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { fetchTeams, fetchTeamWorkload } from "../../store/workOrdersSlice";
import { RootState } from "../../../../core/store";
import { TeamMemberWorkload, Location } from "../../models/models";
import { getTeamColor } from "./SidebarTeamRow";
import { getWorkloadColor } from "./SidebarMemberRow";
import { getAvatarColor, getInitials } from "../shared/AssigneeAvatarStack";
import { getMachines } from "../../../machinery/customerMachinerySlice";
import { CustomerMachineryState, Machine } from "../../../machinery/models/models";
import api from "../../../../core/api";

// ─── Constants ──────────────────────────────────────────────
const PILL_ACTIVE_BG = "#0C1117";
const PILL_INACTIVE_BG = "#F7F5F0";
const PILL_INACTIVE_TEXT = "#4A4F57";
const PILL_INACTIVE_BORDER = "#E8E5DF";
const TERRACOTTA = "#D4572B";
const TERRACOTTA_PINK = "#FFF3EE";
const CARD_BORDER = "#E8E5DF";
const STRIP_BG = "#F7F5F0";

const getCustomersConfig = () => ({
  baseURL: (window as any).app.config.customersApiBaseUrl,
});

/** Resolve the best available thumbnail URL for a machine */
const getMachineThumbnail = (machine: Machine): string | null => {
  return (
    machine.image?.content ||
    machine.catalogModel?.imageStandardUrl ||
    machine.catalogModel?.imageUrl ||
    null
  );
};

// ─── Types ──────────────────────────────────────────────────
type ExpandedSection = "teams" | "machines" | "locations" | null;

interface TabletTeamsBarProps {
  canManage: boolean;
  machineCount?: number;
  locationCount?: number;
  onLocationsLoaded?: (locations: Location[]) => void;
}

const TabletTeamsBar: React.FC<TabletTeamsBarProps> = ({ canManage, machineCount = 0, locationCount = 0, onLocationsLoaded }) => {
  const { translate } = useTranslation("operations");
  const dispatch = useAppDispatch();
  const { teams, teamsLoading } = useAppSelector(
    (state: RootState) => state.workOrders
  );

  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>("teams");

  // Machine search
  const [machineSearch, setMachineSearch] = useState("");
  // Location search
  const [locationSearch, setLocationSearch] = useState("");

  // ─── Machine data ─────────────────────────────────────────
  const { machines: machinesByType, loading: machinesLoading } = useAppSelector<CustomerMachineryState>(
    (state) => (state as any).customerMachinery
  );

  useEffect(() => {
    const hasData = Object.keys(machinesByType).length > 0;
    if (!hasData && !machinesLoading) {
      dispatch(getMachines({}));
    }
  }, [dispatch, machinesByType, machinesLoading]);

  const allMachines = useMemo(
    () => Object.values(machinesByType).flat(),
    [machinesByType]
  );

  const filteredMachines = useMemo(() => {
    if (!machineSearch.trim()) return allMachines;
    const term = machineSearch.toLowerCase().trim();
    return allMachines.filter(
      (m) =>
        m.model?.toLowerCase().includes(term) ||
        m.manufacturer?.name?.toLowerCase().includes(term) ||
        m.serialNumber?.toLowerCase().includes(term) ||
        m.customBrandName?.toLowerCase().includes(term) ||
        m.internalNumber?.toLowerCase().includes(term)
    );
  }, [allMachines, machineSearch]);

  // ─── Location data ────────────────────────────────────────
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // Per-location machine counts from Redux
  const machineCountByLocationId = useMemo(() => {
    const counts: Record<string, number> = {};
    const flat = Object.values(machinesByType || {}).flat() as Machine[];
    flat.forEach((m) => {
      if (m.locationId) {
        counts[m.locationId] = (counts[m.locationId] || 0) + 1;
      }
    });
    return counts;
  }, [machinesByType]);

  const fetchLocations = useCallback(async () => {
    try {
      setLocationsLoading(true);
      const res = await api.get<any[]>("/v1/locations", getCustomersConfig());
      const raw = res.data || [];
      const data: Location[] = raw.map((parent: any) => ({
        ...parent,
        children: (parent.childLocations || []).map((child: any) => ({
          ...child,
          parentLocationName: parent.name,
        })),
      }));
      setLocations(data);
      // Callback to parent for lookup map
      const flat = data.flatMap((parent: Location) => [
        parent,
        ...(parent.children || []),
      ]);
      onLocationsLoaded?.(flat);
    } catch {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  }, [onLocationsLoaded]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Flatten all locations (parents + children) for the card strip
  const allFlatLocations = useMemo(() => {
    return locations.flatMap((parent) => {
      const children = (parent.children || []).map((child) => ({
        ...child,
        parentSiteName: parent.name,
        machineCount: machineCountByLocationId[child.id] || 0,
      }));
      return [
        {
          ...parent,
          parentSiteName: undefined as string | undefined,
          machineCount:
            (machineCountByLocationId[parent.id] || 0) +
            (parent.children || []).reduce(
              (sum, c) => sum + (machineCountByLocationId[c.id] || 0),
              0
            ),
        },
        ...children,
      ];
    });
  }, [locations, machineCountByLocationId]);

  const filteredLocations = useMemo(() => {
    if (!locationSearch.trim()) return allFlatLocations;
    const term = locationSearch.toLowerCase().trim();
    return allFlatLocations.filter(
      (loc) =>
        loc.name?.toLowerCase().includes(term) ||
        loc.parentSiteName?.toLowerCase().includes(term)
    );
  }, [allFlatLocations, locationSearch]);

  // ─── Team data ────────────────────────────────────────────
  useEffect(() => {
    if (teams.length === 0 && !teamsLoading) {
      dispatch(fetchTeams());
    }
  }, [dispatch, teams.length, teamsLoading]);

  useEffect(() => {
    teams.forEach((team) => {
      dispatch(fetchTeamWorkload(team.id));
    });
  }, [dispatch, teams]);

  useEffect(() => {
    if (!selectedTeamId && teams.length > 0) {
      setSelectedTeamId(teams[0].id);
    }
  }, [selectedTeamId, teams]);

  const teamWorkloadMap = useMemo(() => {
    const map: Record<string, TeamMemberWorkload[]> = {};
    teams.forEach((team) => {
      if (team.members && team.members.length > 0) {
        map[team.id] = team.members.map((m) => ({
          userId: m.userId,
          userName: m.userName,
          openCount: m.openWorkOrderCount || 0,
          overdueCount: 0,
          completedCount: 0,
        }));
      } else {
        map[team.id] = [];
      }
    });
    return map;
  }, [teams]);

  const selectedMembers = selectedTeamId
    ? teamWorkloadMap[selectedTeamId] || []
    : [];

  const maxOpenInTeam = selectedMembers.length > 0
    ? Math.max(...selectedMembers.map((m) => m.openCount), 1)
    : 1;

  // ─── Pill click handlers ──────────────────────────────────
  const handlePillClick = useCallback((section: ExpandedSection) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  }, []);

  // Computed counts
  const teamsCount = teams.length;
  const machinesCount = allMachines.length;
  const locsCount = allFlatLocations.length;

  if (teamsCount === 0 && machinesCount === 0 && locsCount === 0) return null;

  // ─── Pill renderer ────────────────────────────────────────
  const renderPill = (
    label: string,
    count: number,
    section: ExpandedSection,
    icon: React.ReactNode
  ) => {
    const isActive = expandedSection === section;
    return (
      <Box
        key={section}
        onClick={(e) => {
          e.stopPropagation();
          handlePillClick(section);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 14px",
          borderRadius: dt.radiusPill,
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "all 0.2s ease",
          backgroundColor: isActive ? PILL_ACTIVE_BG : PILL_INACTIVE_BG,
          color: isActive ? "#FFFFFF" : PILL_INACTIVE_TEXT,
          border: `1px solid ${isActive ? PILL_ACTIVE_BG : PILL_INACTIVE_BORDER}`,
          fontFamily: dt.font,
          fontSize: "12px",
          fontWeight: 600,
          "&:hover": !isActive
            ? { borderColor: "#8C9099", color: "#0C1117" }
            : {},
        }}
      >
        {icon}
        {label}
        <Box
          component="span"
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "10px",
            fontWeight: 700,
            padding: "1px 7px",
            borderRadius: "10px",
            backgroundColor: isActive ? TERRACOTTA : "rgba(140,144,153,0.12)",
            color: isActive ? "#FFFFFF" : PILL_INACTIVE_TEXT,
            lineHeight: "14px",
          }}
        >
          {count}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        marginBottom: "12px",
        animation: "fadeSlideUp 0.3s ease both",
      }}
    >
      {/* ─── Pill Bar (always visible) ─────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 14px",
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {renderPill(
          translate("sidebar.title"),
          teamsCount,
          "teams",
          <GroupsOutlined sx={{ fontSize: 14, color: "inherit" }} />
        )}
        {machinesCount > 0 &&
          renderPill(
            translate("sidebar.machines"),
            machinesCount,
            "machines",
            <PrecisionManufacturingOutlined sx={{ fontSize: 14, color: "inherit" }} />
          )}
        {locsCount > 0 &&
          renderPill(
            translate("sidebar.locations"),
            locsCount,
            "locations",
            <LocationOnOutlined sx={{ fontSize: 14, color: "inherit" }} />
          )}
      </Box>

      {/* ─── Teams Section ─────────────────────────────────── */}
      <Collapse in={expandedSection === "teams"} timeout={200}>
        <Box
          sx={{
            backgroundColor: STRIP_BG,
            borderRadius: "12px",
            margin: "0 8px 4px",
            padding: "6px 10px 8px",
          }}
        >
          {/* Team sub-pills */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              overflowX: "auto",
              paddingBottom: "6px",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {teams.map((team) => {
              const teamColor = getTeamColor(team.id);
              const isSelected = selectedTeamId === team.id;
              return (
                <Box
                  key={team.id}
                  onClick={() => setSelectedTeamId(team.id)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: dt.radiusPill,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    backgroundColor: isSelected ? teamColor : dt.cardBg,
                    color: isSelected ? "#FFFFFF" : dt.textSecondary,
                    border: `1px solid ${isSelected ? teamColor : dt.cardBorder}`,
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight: 600,
                    "&:hover": !isSelected
                      ? { borderColor: teamColor, color: teamColor }
                      : {},
                  }}
                >
                  {!isSelected && (
                    <Box
                      sx={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: teamColor,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  {team.name}
                  <Box
                    component="span"
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "9px",
                      fontWeight: 700,
                      padding: "0px 5px",
                      borderRadius: "8px",
                      backgroundColor: isSelected
                        ? "rgba(255,255,255,0.25)"
                        : `${teamColor}15`,
                      color: isSelected ? "#FFFFFF" : teamColor,
                    }}
                  >
                    {team.memberCount || (team.members ? team.members.length : 0)}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Member avatars — horizontal draggable strip */}
          {selectedTeamId && selectedMembers.length > 0 && (
            <Droppable
              droppableId={`tablet-members-${selectedTeamId}`}
              type="PERSON"
              isDropDisabled
              direction="horizontal"
            >
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    padding: "4px 0",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {selectedMembers.map((member, idx) => {
                    const avatarColor = getAvatarColor(member.userId);
                    const workloadColor = getWorkloadColor(member.openCount);
                    const barWidth = maxOpenInTeam > 0
                      ? Math.min((member.openCount / maxOpenInTeam) * 100, 100)
                      : 0;

                    return (
                      <Draggable
                        key={`${selectedTeamId}-${member.userId}`}
                        draggableId={`person::${selectedTeamId}::${member.userId}`}
                        index={idx}
                      >
                        {(dragProvided, snapshot) => {
                          const draggableElement = (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "3px",
                                cursor: snapshot.isDragging ? "grabbing" : "grab",
                                flexShrink: 0,
                                minWidth: "44px",
                                transform: snapshot.isDragging ? "rotate(2deg)" : "none",
                                opacity: snapshot.isDragging ? 0.92 : 1,
                                boxShadow: snapshot.isDragging
                                  ? "0 8px 24px rgba(0,0,0,0.15)"
                                  : "none",
                                backgroundColor: snapshot.isDragging ? "#FFFFFF" : "transparent",
                                borderRadius: snapshot.isDragging ? "8px" : "0",
                                padding: snapshot.isDragging ? "4px" : "0",
                                zIndex: snapshot.isDragging ? 9999 : undefined,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "50%",
                                  backgroundColor: avatarColor,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  fontFamily: dt.font,
                                  color: "#FFFFFF",
                                }}
                              >
                                {getInitials(member.userName)}
                              </Box>
                              <Box
                                sx={{
                                  fontFamily: dt.font,
                                  fontSize: "9px",
                                  fontWeight: 500,
                                  color: dt.textSecondary,
                                  maxWidth: "44px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  textAlign: "center",
                                }}
                              >
                                {member.userName.split(" ")[0]}
                              </Box>
                              <Box
                                sx={{
                                  width: "28px",
                                  height: "4px",
                                  backgroundColor: dt.cardBorder,
                                  borderRadius: "2px",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${Math.max(barWidth, member.openCount > 0 ? 10 : 0)}%`,
                                    height: "100%",
                                    backgroundColor: workloadColor,
                                    borderRadius: "2px",
                                    transition: "width 0.3s ease",
                                  }}
                                />
                              </Box>
                            </Box>
                          );

                          if (snapshot.isDragging) {
                            return createPortal(draggableElement, document.body);
                          }
                          return draggableElement;
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          )}

          {selectedTeamId && selectedMembers.length === 0 && (
            <Box
              sx={{
                padding: "6px 0",
                fontFamily: dt.font,
                fontSize: "11px",
                color: dt.textMuted,
                fontStyle: "italic",
              }}
            >
              {translate("sidebar.noTeams")}
            </Box>
          )}
        </Box>
      </Collapse>

      {/* ─── Machines Section (horizontal card strip) ──────── */}
      <Collapse in={expandedSection === "machines"} timeout={200}>
        <Box
          sx={{
            backgroundColor: STRIP_BG,
            borderRadius: "12px",
            margin: "0 8px 4px",
            padding: "8px 12px",
          }}
        >
          {/* Search + hint row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 10px",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
                border: `1px solid ${CARD_BORDER}`,
                flex: "0 0 auto",
                maxWidth: "200px",
                transition: "box-shadow 0.15s ease",
                "&:focus-within": { boxShadow: "0 0 0 1.5px #3B82F6" },
              }}
            >
              <SearchOutlined sx={{ fontSize: 13, color: "#8C9099", flexShrink: 0 }} />
              <Box
                component="input"
                value={machineSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMachineSearch(e.target.value)}
                placeholder={translate("machineSidebar.search")}
                sx={{
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "#0C1117",
                  width: "100%",
                  padding: 0,
                  "&::placeholder": { color: "#8C9099" },
                }}
              />
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 400,
                color: "#8C9099",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {translate("machineSidebar.dragToAssign")}
            </Box>
          </Box>

          {/* Horizontal machine card strip */}
          <Droppable droppableId="tablet-machines" type="PERSON" isDropDisabled direction="horizontal">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: "4px",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {filteredMachines.length === 0 ? (
                    <Box
                      sx={{
                        padding: "16px 0",
                        fontFamily: dt.font,
                        fontSize: "12px",
                        color: "#8C9099",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {translate("machineSidebar.noMachines")}
                    </Box>
                  ) : (
                    filteredMachines.map((machine, idx) => (
                      <Draggable
                        key={machine.id}
                        draggableId={`machine::${machine.id}`}
                        index={idx}
                      >
                        {(dragProvided, snapshot) => {
                          const thumb = getMachineThumbnail(machine);
                          const card = (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 10px",
                                backgroundColor: "#FFFFFF",
                                border: `1px solid ${CARD_BORDER}`,
                                borderRadius: "8px",
                                cursor: snapshot.isDragging ? "grabbing" : "grab",
                                flexShrink: 0,
                                minWidth: "160px",
                                maxWidth: "200px",
                                transition: snapshot.isDragging ? "none" : "box-shadow 0.15s ease",
                                transform: snapshot.isDragging ? "rotate(2deg)" : "none",
                                boxShadow: snapshot.isDragging
                                  ? "0 8px 24px rgba(0,0,0,0.18)"
                                  : "0 1px 2px rgba(0,0,0,0.04)",
                                opacity: snapshot.isDragging ? 0.92 : 1,
                                zIndex: snapshot.isDragging ? 9999 : undefined,
                                "&:hover": snapshot.isDragging
                                  ? {}
                                  : { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                              }}
                            >
                              {/* Machine thumbnail 40x40 */}
                              {thumb ? (
                                <Box
                                  component="img"
                                  src={thumb}
                                  alt={machine.model}
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "6px",
                                    objectFit: "cover",
                                    flexShrink: 0,
                                    backgroundColor: "#F0EDE8",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "6px",
                                    backgroundColor: "#EDEAE4",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <PrecisionManufacturingOutlined sx={{ fontSize: 18, color: "#8C9099" }} />
                                </Box>
                              )}
                              {/* Model + SN */}
                              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                                <Box
                                  sx={{
                                    fontFamily: dt.font,
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#0C1117",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    lineHeight: "16px",
                                  }}
                                >
                                  {machine.model}
                                </Box>
                                {machine.serialNumber && (
                                  <Box
                                    sx={{
                                      fontFamily: dt.fontMono,
                                      fontSize: "11px",
                                      fontWeight: 400,
                                      color: "#8C9099",
                                      lineHeight: "14px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    SN:{machine.serialNumber}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );

                          if (snapshot.isDragging) {
                            return createPortal(card, document.body);
                          }
                          return card;
                        }}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </Box>
                {/* Right fade overlay for scroll hint */}
                {filteredMachines.length > 3 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: "48px",
                      height: "100%",
                      background: `linear-gradient(90deg, transparent, ${STRIP_BG})`,
                      pointerEvents: "none",
                    }}
                  />
                )}
              </Box>
            )}
          </Droppable>
        </Box>
      </Collapse>

      {/* ─── Locations Section (horizontal card strip) ─────── */}
      <Collapse in={expandedSection === "locations"} timeout={200}>
        <Box
          sx={{
            backgroundColor: STRIP_BG,
            borderRadius: "12px",
            margin: "0 8px 4px",
            padding: "8px 12px",
          }}
        >
          {/* Search + hint row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 10px",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
                border: `1px solid ${CARD_BORDER}`,
                flex: "0 0 auto",
                maxWidth: "200px",
                transition: "box-shadow 0.15s ease",
                "&:focus-within": { boxShadow: "0 0 0 1.5px #3B82F6" },
              }}
            >
              <SearchOutlined sx={{ fontSize: 13, color: "#8C9099", flexShrink: 0 }} />
              <Box
                component="input"
                value={locationSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocationSearch(e.target.value)}
                placeholder={translate("locationSidebar.search")}
                sx={{
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  fontFamily: dt.font,
                  fontSize: "12px",
                  fontWeight: 400,
                  color: "#0C1117",
                  width: "100%",
                  padding: 0,
                  "&::placeholder": { color: "#8C9099" },
                }}
              />
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 400,
                color: "#8C9099",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {translate("locationSidebar.dragToAssign")}
            </Box>
          </Box>

          {/* Horizontal location card strip */}
          <Droppable droppableId="tablet-locations" type="PERSON" isDropDisabled direction="horizontal">
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: "4px",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {filteredLocations.length === 0 ? (
                    <Box
                      sx={{
                        padding: "16px 0",
                        fontFamily: dt.font,
                        fontSize: "12px",
                        color: "#8C9099",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      {translate("locationSidebar.noLocations")}
                    </Box>
                  ) : (
                    filteredLocations.map((loc, idx) => (
                      <Draggable
                        key={loc.id}
                        draggableId={`location::${loc.id}`}
                        index={idx}
                      >
                        {(dragProvided, snapshot) => {
                          const card = (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "8px 10px",
                                backgroundColor: "#FFFFFF",
                                border: `1px solid ${CARD_BORDER}`,
                                borderRadius: "8px",
                                cursor: snapshot.isDragging ? "grabbing" : "grab",
                                flexShrink: 0,
                                minWidth: "180px",
                                maxWidth: "220px",
                                transition: snapshot.isDragging ? "none" : "box-shadow 0.15s ease",
                                transform: snapshot.isDragging ? "rotate(2deg)" : "none",
                                boxShadow: snapshot.isDragging
                                  ? "0 8px 24px rgba(0,0,0,0.18)"
                                  : "0 1px 2px rgba(0,0,0,0.04)",
                                opacity: snapshot.isDragging ? 0.92 : 1,
                                zIndex: snapshot.isDragging ? 9999 : undefined,
                                "&:hover": snapshot.isDragging
                                  ? {}
                                  : { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                              }}
                            >
                              {/* Terracotta pin icon on warm pink bg */}
                              <Box
                                sx={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  backgroundColor: TERRACOTTA_PINK,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <PlaceOutlined sx={{ fontSize: 18, color: TERRACOTTA }} />
                              </Box>
                              {/* Site + hall + machine count */}
                              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }}>
                                <Box
                                  sx={{
                                    fontFamily: dt.font,
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#0C1117",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    lineHeight: "16px",
                                  }}
                                >
                                  {loc.parentSiteName || loc.name}
                                </Box>
                                {loc.parentSiteName && (
                                  <Box
                                    sx={{
                                      fontFamily: dt.font,
                                      fontSize: "12px",
                                      fontWeight: 400,
                                      color: "#4A4F57",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      lineHeight: "15px",
                                    }}
                                  >
                                    {loc.name}
                                  </Box>
                                )}
                                {(loc.machineCount ?? 0) > 0 && (
                                  <Box
                                    sx={{
                                      fontFamily: dt.fontMono,
                                      fontSize: "10px",
                                      fontWeight: 500,
                                      color: "#8C9099",
                                      lineHeight: "13px",
                                    }}
                                  >
                                    {loc.machineCount} {(loc.machineCount ?? 0) === 1 ? "machine" : "machines"}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );

                          if (snapshot.isDragging) {
                            return createPortal(card, document.body);
                          }
                          return card;
                        }}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </Box>
                {/* Right fade overlay for scroll hint */}
                {filteredLocations.length > 3 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: "48px",
                      height: "100%",
                      background: `linear-gradient(90deg, transparent, ${STRIP_BG})`,
                      pointerEvents: "none",
                    }}
                  />
                )}
              </Box>
            )}
          </Droppable>
        </Box>
      </Collapse>
    </Box>
  );
};

export default TabletTeamsBar;
